import React from 'react';
import VolumeUpRoundedIcon from '@mui/icons-material/VolumeUpRounded';
import VolumeOffRoundedIcon from '@mui/icons-material/VolumeOffRounded';
import {
  StyledVolumePopover,
  StyledMuteButton,
  StyledSlider,
  StyledValue,
} from './index.style';

/**
 * A track's volume control, opened from its gutter button. The speaker icon
 * toggles mute, the slider sets the level (0..1), and the percentage mirrors it.
 * Presentational — the level lives in `useTrackVolume`, so a track and its mute
 * button stay in sync.
 */
const VolumePopover = ({
  open,
  anchorEl,
  volume = 0,
  muted = false,
  onClose,
  onVolumeChange,
  onVolumeCommit,
  onToggleMute,
  onMouseEnter,
  onMouseLeave,
}) => {
  const percent = Math.round(volume * 100);

  return (
    <StyledVolumePopover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'center', horizontal: 'right' }}
      transformOrigin={{ vertical: 'center', horizontal: 'left' }}
      // Hover popover: no backdrop / focus trap, and the root ignores pointer
      // events (see index.style) so hovering the gutter button underneath keeps
      // working; the paper re-enables them and tracks its own hover to stay open.
      hideBackdrop
      disableAutoFocus
      disableEnforceFocus
      disableRestoreFocus
      disableScrollLock
      PaperProps={{ onMouseEnter, onMouseLeave }}
    >
      <StyledMuteButton
        type="button"
        aria-label="toggle mute"
        onClick={onToggleMute}
      >
        {muted ? <VolumeOffRoundedIcon /> : <VolumeUpRoundedIcon />}
      </StyledMuteButton>
      <StyledSlider
        size="small"
        aria-label="Volume"
        value={percent}
        min={0}
        max={100}
        onChange={(event, value) => onVolumeChange?.(value / 100)}
        // Pointer release ends the gesture: closes the coalesced undo step so
        // one drag is one undo, not one per pointer move.
        onChangeCommitted={() => onVolumeCommit?.()}
      />
      <StyledValue>{`${percent}%`}</StyledValue>
    </StyledVolumePopover>
  );
};

export default VolumePopover;
